import type { MayaFormField, MayaFormSpec } from "./types";

// Backend caps a chat message at 2000 chars (AiChatDto @MaxLength(2000)).
export const MAYA_FORM_MAX_MSG = 2000;

// Inline note that replaces the raw JSON fence so users never see machine text.
export const MAYA_FORM_INLINE_NOTE = "_Fill in the form below to continue._";

const SINGLE_CHECKBOX_TRUTHY = /^(1|true|yes|on|checked)$/i;

/**
 * The single acceptance gate for BOTH delivery channels (fenced block + SSE
 * `form` field). A value is a form spec only if it has a non-empty `fields`
 * array whose every entry is an object carrying at least one of type/name/label.
 */
export function looksLikeFormSpec(obj: unknown): obj is MayaFormSpec {
  if (!obj || typeof obj !== "object") return false;
  const fields = (obj as { fields?: unknown }).fields;
  return (
    Array.isArray(fields) &&
    fields.length > 0 &&
    fields.every(
      (f) =>
        f &&
        typeof f === "object" &&
        ("type" in f || "name" in f || "label" in f),
    )
  );
}

export const isSingleCheckbox = (f: MayaFormField): boolean =>
  (f.type ?? "").toLowerCase() === "checkbox" &&
  !(Array.isArray(f.options) && f.options.length > 0);

export const fieldLabel = (f: MayaFormField, idx: number): string =>
  (f.label ?? f.name ?? `Field ${idx + 1}`).toString();

export const isSingleCheckboxChecked = (raw: string): boolean =>
  SINGLE_CHECKBOX_TRUTHY.test(raw.trim());

// Matches a markdown fenced code block, capturing its info string + body.
const FENCE_RE = /```([^\n`]*)\r?\n([\s\S]*?)```/g;
const MAYA_FORM_LANG_RE = /^maya-?form$/;

const parseFormBody = (body: string): MayaFormSpec | null => {
  const t = body.trim();
  if (t.length < 2 || t[0] !== "{") return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(t);
  } catch {
    return null;
  }
  return looksLikeFormSpec(parsed) ? parsed : null;
};

/**
 * Scan answer markdown for a form spec. Prefers a ```maya-form-tagged fence;
 * falls back to the first fenced block of any language whose JSON passes the
 * gate (the model sometimes mislabels the fence as ```json or leaves it blank).
 * Returns the parsed spec and the content with that fence swapped for a
 * friendly inline note. Never throws — a malformed spec leaves content as-is.
 */
export function detectFencedForm(markdown: string): {
  spec: MayaFormSpec | null;
  content: string;
} {
  if (!markdown || !markdown.includes("```")) {
    return { spec: null, content: markdown };
  }
  try {
    const blocks: {
      full: string;
      lang: string;
      body: string;
      index: number;
    }[] = [];
    FENCE_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = FENCE_RE.exec(markdown)) !== null) {
      blocks.push({
        full: m[0],
        lang: (m[1] ?? "").trim().toLowerCase(),
        body: m[2],
        index: m.index,
      });
    }
    if (blocks.length === 0) return { spec: null, content: markdown };

    let chosen: { block: (typeof blocks)[number]; spec: MayaFormSpec } | null =
      null;
    // 1. Prefer an explicitly tagged maya-form fence.
    for (const b of blocks) {
      if (MAYA_FORM_LANG_RE.test(b.lang)) {
        const spec = parseFormBody(b.body);
        if (spec) {
          chosen = { block: b, spec };
          break;
        }
      }
    }
    // 2. Fallback: first fenced JSON that looks like a form spec.
    if (!chosen) {
      for (const b of blocks) {
        const spec = parseFormBody(b.body);
        if (spec) {
          chosen = { block: b, spec };
          break;
        }
      }
    }
    if (!chosen) return { spec: null, content: markdown };

    const content =
      markdown.slice(0, chosen.block.index) +
      MAYA_FORM_INLINE_NOTE +
      markdown.slice(chosen.block.index + chosen.block.full.length);
    return { spec: chosen.spec, content };
  } catch {
    return { spec: null, content: markdown };
  }
}

/**
 * Compose resolved field values into the "Label: value" message that gets
 * dropped into the composer. `resolved[i]` is the already-formatted string for
 * field i (""=empty, "Yes"/"No" for a single checkbox, comma-joined for
 * multi-checkbox). Empty values render as "—". `submit_prefix` is prepended.
 */
export function composeSubmission(
  spec: MayaFormSpec,
  resolved: string[],
): { text: string; trimmed: boolean } {
  const lines = spec.fields.map(
    (f, i) => `${fieldLabel(f, i)}: ${resolved[i] ? resolved[i] : "—"}`,
  );
  let text =
    (spec.submit_prefix ? `${spec.submit_prefix}\n` : "") + lines.join("\n");
  let trimmed = false;
  if (text.length > MAYA_FORM_MAX_MSG) {
    text = text.slice(0, MAYA_FORM_MAX_MSG);
    trimmed = true;
  }
  return { text, trimmed };
}
